// Report-specific column configurations with overrides
const reportColumnConfigs = {
  accounts: {
    columns: [
      'Account Name',
      'Account Number', 
      'Sleeve Name',
      'Holdings Current Value',
      'Target Weight',
      'Actual Weight (of Assigned)',
      'Trade Action'
    ],
    overrides: {}
  },
  securities: {
    columns: [
      'Security Level Model Name',
      'Sleeve Name',
      //'Account Names',
      // 'Quantity',
      // 'Price',
      'Holdings Current Value',
      'Target Weight',
      'Actual Weight',
      //'Actual Weight (of Assigned)',
      'Trade Action With Restriction',
      //'Trade Action With Restriction (of Assigned)',
    ],
    overrides: {}
  },
  cashActions: {
    columns: [
      'Account Name',
      'Account Number',
      'Cash Action',
      'Holdings Current Value',
      'Calculated Reserve Center',
      'Calculated Reserve Min',
      'Calculated Reserve Max'
    ],
    overrides: {
      'Holdings Current Value': { label: 'Current Cash', description: 'Current cash balance in the account' }
    }
  },
  tradeRestrictions: {
    columns: [
      'Symbol',
      'Security Level Model Name',
      'Sleeve Name',
      'Account Names',
      'Quantity',
      'Holdings Current Value',
      'Restriction',
      'Restriction Note',
      'Restriction Note Added By'
    ],
    overrides: {
      'Action Type': { label: 'Action Type', description: 'Type of trading action required (Buy/Sell)' }
    }
  },
  sleeves: {
    columns: [
      'Sleeve Name',
      'Account Names',
      'Holdings Current Value',
      'Target Weight',
      'Actual Weight (of Assigned)'
    ],
    overrides: {}
  }
};

// Format currency values for display
export const formatCurrency = (value, hideDollarValues = false) => {
  if (typeof value !== 'number') return value;
  if (hideDollarValues) return '***';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

// Custom styling function for trade action badges
export const getTradeActionStyles = (className) => {
  const styles = {
    'trade-action-buy': {
      backgroundColor: '#198754',
      color: 'white'
    },
    'trade-action-sell': {
      backgroundColor: '#dc3545', 
      color: 'white'
    },
    'trade-action-hold': {
      backgroundColor: '#6c757d',
      color: 'white'
    },
    'trade-action-buy-weight': {
      backgroundColor: '#d1eddd',
      color: '#0f5132',
      border: '1px solid #a3cfbb'
    },
    'trade-action-sell-weight': {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      border: '1px solid #f1aeb5'
    },
    'trade-action-conflict': {
      backgroundColor: '#FFA500',
      color: 'white',
      border: '1px solid #ffecb5'
    },
    'trade-action-unknown': {
      backgroundColor: '#f8f9fa',
      color: '#495057',
      border: '1px solid #dee2e6'
    }
  };
  
  return styles[className] || {};
};

// Report options for dropdown
export const reportOptions = [
  { value: 'accounts', label: 'Group By Accounts' }
];

// Helper function to get column definitions with proper currency formatting context
export const getColumnDefinitionsWithFormatting = (hideDollarValues = false) => {
  const currencyFormatter = (value) => formatCurrency(value, hideDollarValues);
  
  return {
    // Common account/sleeve identifiers
    'Account Name': { key: 'Account Name', label: 'Account Name', description: 'Name of the account', sortable: true, initialWidth: 200 },
    'Account Number': { key: 'Account Number', label: 'Account Number', description: 'Account identification number', sortable: true, initialWidth: 150 },
    'Sleeve Name': { key: 'Sleeve Name', label: 'Sleeve Name', description: 'Name of the investment sleeve', sortable: true, initialWidth: 100 },
    'Account Names': { key: 'Account Names', label: 'Account Names', description: 'Names of accounts in this sleeve', sortable: false, initialWidth: 200 },
    
    // Financial values
    'Holdings Current Value': { key: 'Holdings Current Value', label: 'Holdings Value', description: 'Current market value of all holdings', sortable: true, initialWidth: 100, format: currencyFormatter },
    'Holdings Current Value_assigned': { key: 'Holdings Current Value_assigned', label: 'Assigned Holdings', description: 'Current market value of assigned holdings only', sortable: true, initialWidth: 150, format: currencyFormatter },
    'Total Cash Reserve Goal Dollar': { key: 'Total Cash Reserve Goal Dollar', label: 'Cash Reserve Goal', description: 'Target cash reserve amount', sortable: true, initialWidth: 150, format: currencyFormatter },
      
    // Weight/percentage columns
    'Target Weight': { key: 'Target Weight', label: 'Target Weight (%)', description: 'Target allocation percentage', sortable: true, initialWidth: 150, format: (value) => `${value}%` },
    'Minimum Target Weight': { key: 'Minimum Target Weight', label: 'Min Target Weight (%)', description: 'Minimum target allocation percentage', sortable: true, initialWidth: 180, format: (value) => `${value}%` },
    'Maximum Target Weight': { key: 'Maximum Target Weight', label: 'Max Target Weight (%)', description: 'Maximum target allocation percentage', sortable: true, initialWidth: 180, format: (value) => `${value}%` },
    'Actual Weight': { key: 'Actual Weight', label: 'Actual Weight (%)', description: 'Current allocation percentage', sortable: true, sortBy: 'difference', initialWidth: 180, format: (value) => `${value}%` },
    'Actual Weight (of Assigned)': { key: 'Actual Weight (of Assigned)', label: 'Actual Weight of Assigned (%)', description: 'Current allocation percentage of assigned holdings only', sortable: true, sortBy: 'difference', initialWidth: 180, format: (value) => `${value}%` },
    
    // Securities-specific columns
    'Symbol': { key: 'Symbol', label: 'Symbol', description: 'Security ticker symbol', sortable: true, initialWidth: 75 },
    'Security Level Model Name': { key: 'Security Level Model Name', label: 'Security Level Model', description: 'Name of the security level model', sortable: true, initialWidth: 200 },
    'Quantity': { key: 'Quantity', label: 'Quantity', description: 'Number of shares/units held', sortable: true, initialWidth: 75 },
    'Price': { key: 'Price', label: 'Price', description: 'Current market price per share/unit', sortable: true, initialWidth: 100, format: currencyFormatter },
      
    // Action Columns
    'Cash Action': { key: 'Cash Action', label: 'Action', description: 'Required cash action (Raise Cash or Invest Cash)', sortable: true, initialWidth: 120 },
    'Trade Action': { key: 'Trade Action', label: 'Action', description: 'Action to take for the trade (Buy, Sell, Hold)', sortable: true, initialWidth: 120 },
    'Action Type': { key: 'Action Type', label: 'Action Type', description: 'Type of trading action required (Buy/Sell)', sortable: true, initialWidth: 120 },
    
    // Cash actions columns
    'Calculated Reserve Center': { key: 'Calculated Reserve Center', label: 'Target Reserve', description: 'Target cash reserve amount', sortable: true, initialWidth: 150, format: currencyFormatter },
    'Calculated Reserve Min': { key: 'Calculated Reserve Min', label: 'Min Reserve', description: 'Minimum cash reserve threshold', sortable: true, initialWidth: 150, format: currencyFormatter },
    'Calculated Reserve Max': { key: 'Calculated Reserve Max', label: 'Max Reserve', description: 'Maximum cash reserve threshold', sortable: true, initialWidth: 150, format: currencyFormatter },

    // Trade restrictions columns
    'Trade Action With Restriction': { key: 'Trade Action With Restriction', label: 'Trade Action with Restriction', description: 'Trade action considering restrictions (e.g., Buy, Sell, Hold)', sortable: true, initialWidth: 250 },
    'Trade Action With Restriction (of Assigned)': { key: 'Trade Action With Restriction (of Assigned)', label: 'Trade Action with Restriction (of Assigned)', description: 'Trade action considering restrictions (e.g., Buy, Sell, Hold)', sortable: true, initialWidth: 250 },
    'Restriction': { key: 'Restriction', label: 'Restriction', description: 'Type of trading restriction (Model/Client)', sortable: true, initialWidth: 250 },
    'Model Restriction': { key: 'Model Restriction', label: 'Model Restriction', description: 'Model-imposed trading restrictions', sortable: true, initialWidth: 200 },
    'Client Restriction': { key: 'Client Restriction', label: 'Client Restriction', description: 'Client-imposed trading restrictions', sortable: true, initialWidth: 200 },
    'Security Restriction Type': { key: 'Security Restriction Type', label: 'Security Restriction Type', description: 'Type of security restriction (e.g., Buy, Sell, Hold)', sortable: true, initialWidth: 200 },
    'Restriction Minium Weight': { key: 'Restriction Minium Weight', label: 'Min Weight (%)', description: 'Minimum weight for trading restrictions', sortable: true, initialWidth: 150, format: (value) => `${value}%` },
    'Restriction Maximum Weight': { key: 'Restriction Maximum Weight', label: 'Max Weight (%)', description: 'Maximum weight for trading restrictions', sortable: true, initialWidth: 150, format: (value) => `${value}%` },
    'Restriction Note': { key: 'Restriction Note', label: 'Restriction Note', description: 'Notes on trading restrictions', sortable: true, initialWidth: 200 },
    'Restriction Note Added By': { key: 'Restriction Note Added By', label: 'Added By', description: 'User who added the restriction note', sortable: true, initialWidth: 100 }
  };
};

// Helper function to get report columns with proper formatting context
export const getReportColumnsWithFormatting = (reportType, hideDollarValues = false) => {
  const config = reportColumnConfigs[reportType];
  if (!config) return [];

  const columnDefinitions = getColumnDefinitionsWithFormatting(hideDollarValues);

  return config.columns.map(columnKey => {
    const baseColumn = columnDefinitions[columnKey];
    const override = config.overrides?.[columnKey];
    
    if (!baseColumn) {
      console.warn(`Base column definition not found for: ${columnKey}`);
      return null;
    }

    return {
      ...baseColumn,
      ...override
    };
  }).filter(Boolean);
};
